import React, {useContext, useEffect} from 'react';
import {Box, Divider, Grid, TextField, Typography, useTheme} from "@mui/material";
import ElionLogo from "../../../assets/rebranding/Elion_Logo_WordmarkBaseline_RGB_Red.svg";
import {SiteResponse} from "../../../services";
import {useTranslation} from "react-i18next";
import {AuthContext, IAuthContext} from "react-oauth2-code-pkce";
import useData from "../../../hooks/useData";
import ElionSwitchSvg from "../../../assets/images/elionSwitch.svg"
import PhoneCallingSvg from "../../../assets/images/phone-calling.svg"
import InfoSvg from "../../../assets/images/info.svg"
import SearchSvg from "../../../assets/images/search.svg"
import LogoutSvg from "../../../assets/images/logout.svg";
import HamburgerSvg from "../../../assets/images/burger-menu.svg";
import {isMobile} from "react-device-detect";
import {CustomTheme} from "../../../config/theme/theme";
import {useNavigate} from "react-router-dom";

interface NavigationProps {
    activePage: string;
    setActivePage: (param1: React.MouseEvent<HTMLDivElement>, param2: string) => void;
    showNavigation: boolean;
    setShowNavigation: (param1: boolean) => void;
    pageRef: any;
}

export default function Navigation(props: NavigationProps) {

    const {isAdvanced} = useData();

    const {t} = useTranslation();
    const theme: CustomTheme = useTheme();

    const {logOut}: IAuthContext = useContext(AuthContext);
    const navigate = useNavigate();

    const {smartBoxes, activeSmartBox, setActiveSmartBox, isInstaller, clearInstallerToken} = useData();

    const [filteredSmartBoxes, setFilteredSmartBoxes] = React.useState<SiteResponse[]>([]);

    useEffect(() => {
        if (smartBoxes !== null) {
            setFilteredSmartBoxes(smartBoxes);
        }
    }, [smartBoxes]);

    function applySmartboxFilter(filter: string): void {
        if (filter) {
            let filtered = smartBoxes.filter((sb: SiteResponse) => {
                return (sb.account + " - " + sb.serialId).toLowerCase().includes(filter.toLowerCase())
            });
            setFilteredSmartBoxes(filtered);
        } else {
            setFilteredSmartBoxes(smartBoxes);
        }
    }

    function onPageClick(event: React.MouseEvent<HTMLDivElement>, page: string) {
        props.setActivePage(event, page);
        if (isMobile) {
            props.setShowNavigation(false);
        }
        // TODO: see how we can get this working for mobile
        // props.pageRef.current.scrollIntoView();
    }

    function logout() {
        logOut();
        if (isInstaller) {
            clearInstallerToken();
            navigate("/installer/login");
        } else {
            navigate("/login");
        }
    }

    return (
        <>
            <Grid width="100%" height="100vh" display="flex" flexDirection="column" pt={2}>
                <Grid m={2} flexGrow={0} sx={{width: '30px', '&:hover': {cursor: 'pointer'}, zIndex: '100 !important'}}
                      onClick={() => props.setShowNavigation(!props.showNavigation)} item>
                    <Box pl={2} sx={{display: {lg: 'none', xs: 'unset'}}}>
                        <img width="100%" src={HamburgerSvg}></img>
                    </Box>
                </Grid>
                <Grid display="flex" justifyContent="center" flexGrow={0} pl={4} pr={4} sx={{display: {lg: 'flex', xs: 'none'}}} item>
                    <img src={ElionLogo} width='90%'></img>
                </Grid>
                <Grid pl={6} pt={6} pb={6} flexGrow={1} minHeight={0} sx={{paddingRight: {xl: 3, lg: 0, md: 0, xs: 3}}}
                      item>
                    <Grid width="100%" height="100%" display="flex" flexDirection="column" gap={2}>
                        <Grid id="smartboxes" flexGrow={1} minHeight={0} item>
                            <Grid width="100%" height="100%" display="flex" flexDirection="column" gap={1}>
                                <Grid id="smartboxesHeader" item>
                                    <Typography fontSize="0.8em"
                                                color={theme.palette.text.primary}>{t('dashboard.navigation.smartboxes')}</Typography>
                                </Grid>
                                <Grid pb={1} minHeight={0} item>
                                    <Grid width="100%" height="100%" display="flex" flexDirection="column" gap={1}>
                                        {(smartBoxes.length > 5) &&
                                            <Grid width="100%" display="flex" alignItems="center" flexGrow={0} pt={1}
                                                  pl={3}
                                                  pr={3} gap={2} borderRadius="5px"
                                                  item>
                                                <Box display="flex" alignItems="center" minWidth="20px" maxWidth="30px"
                                                     width="8%">
                                                    <img width="100%" src={SearchSvg}/>
                                                </Box>
                                                <TextField hiddenLabel id="search" style={{width: '100%'}}
                                                           variant="standard" size="small"
                                                           inputProps={{style: {color: theme.palette.text.primary}}}
                                                           onChange={(event: React.ChangeEvent<HTMLInputElement>) => applySmartboxFilter(event.target.value)}/>
                                            </Grid>}
                                        <Grid width="100%" display="flex" flexDirection="column" flexGrow={1}
                                              minHeight={0} gap={1}
                                              sx={{overflowY: 'auto'}} item>
                                            {
                                                (Array.isArray(filteredSmartBoxes) ? filteredSmartBoxes : [])?.map((smartBox: SiteResponse) => (
                                                    <Grid key={smartBox.serialId + smartBox.account} display="flex"
                                                          alignItems="center"
                                                          bgcolor={smartBox === activeSmartBox ? theme.palette.navigation.selected : "transparent"}
                                                          pt={1}
                                                          pb={1} pl={3}
                                                          pr={3} gap={2} borderRadius="5px "
                                                          onClick={() => {
                                                              setActiveSmartBox(smartBox);
                                                              if (isMobile) {
                                                                  props.setShowNavigation(false);
                                                              }
                                                          }}
                                                          sx={{'&:hover': {cursor: 'pointer'}}}
                                                          item>
                                                        <Box display="flex" alignItems="center" minWidth="20px"
                                                             maxWidth="30px" width="6%">
                                                            <img width="100%" src={ElionSwitchSvg}/></Box>
                                                        <Typography fontWeight="700" fontSize="0.7em"
                                                                    color={theme.palette.text.primary}>{smartBox.account} - {smartBox.serialId}</Typography>
                                                    </Grid>
                                                ))
                                            }
                                            {
                                                ((!filteredSmartBoxes) || (filteredSmartBoxes.length == 0)) &&
                                                <Grid item>
                                                    <Typography fontWeight="700" fontSize="0.7em"
                                                                color={theme.palette.text.primary}>{t('dashboard.navigation.noAccountsFound')}</Typography>
                                                </Grid>
                                            }
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider/>
                                <Grid id="pages" pt={1} item>
                                    <Grid width="100%" display="flex" flexDirection="column" gap={1}>
                                        <Grid id="smartboxesHeader" item>
                                            <Typography fontSize="0.8em"
                                                        color={theme.palette.text.primary}>{t('dashboard.navigation.pages')}</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Grid width="100%" display="flex" flexDirection="column" gap={1}>
                                                <Grid display="flex" alignItems="center"
                                                      bgcolor={props.activePage === 'technical' ? theme.palette.navigation.selected : "transparent"}
                                                      pt={1}
                                                      pb={1} pl={3}
                                                      pr={3} gap={2} borderRadius="5px "
                                                      onClick={(event: React.MouseEvent<HTMLDivElement>) => onPageClick(event, 'technical')}
                                                      sx={{'&:hover': {cursor: 'pointer'}}}
                                                      item>
                                                    <Typography fontWeight="700" fontSize="0.7em"
                                                                color={theme.palette.text.primary}>{t('dashboard.technicalInformation')}</Typography>
                                                </Grid>
                                                <Grid display="flex" alignItems="center"
                                                      bgcolor={props.activePage === 'financial' ? theme.palette.navigation.selected : "transparent"}
                                                      pt={1}
                                                      pb={1} pl={3}
                                                      pr={3} gap={2} borderRadius="5px"
                                                      onClick={(event: React.MouseEvent<HTMLDivElement>) => onPageClick(event, 'financial')}
                                                      sx={{'&:hover': {cursor: 'pointer'}}}
                                                      item>
                                                    <Typography fontWeight="700" fontSize="0.7em"
                                                                color={theme.palette.text.primary}>{t('dashboard.financialInformation')}</Typography>
                                                </Grid>
                                                {isAdvanced && <Grid display="flex" alignItems="center"
                                                      bgcolor={props.activePage === 'advanced' ? theme.palette.navigation.selected : "transparent"}
                                                      pt={1}
                                                      pb={1} pl={3}
                                                      pr={3} gap={2} borderRadius="5px"
                                                      onClick={(event: React.MouseEvent<HTMLDivElement>) => onPageClick(event, 'advanced')}
                                                      sx={{'&:hover': {cursor: 'pointer'}}}
                                                      item>
                                                    <Typography fontWeight="700" fontSize="0.7em"
                                                                color={theme.palette.text.primary}>{t('dashboard.advancedInformation')}</Typography>
                                                </Grid>}

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider/>
                        <Grid id="generalSettings" flexGrow={0} item>
                            <Grid width="100%" display="flex" flexDirection="column" gap={2}>
                                <Grid display="flex" gap={2} onClick={() => {
                                    window.open('https://klant.elindus.be/s/contacteer-ons?language=nl_NL', '_blank');
                                }} sx={{'&:hover': {cursor: 'pointer', bgcolor: theme.palette.navigation.selected}}} item>
                                    {/* <img src={PhoneCallingSvg} style={{width: '1.2em'}}/> */}
                                    <Typography fontSize="0.9em" style={{textDecoration: 'underline', textUnderlineOffset: '2px'}}
                                                color={theme.palette.text.primary}>{t('dashboard.navigation.contactUs')}</Typography>
                                </Grid>
                                <Grid display="flex" gap={2} onClick={() => {
                                    window.open('https://klant.elindus.be/s/veelgestelde-vragen?language=nl_NL', '_blank');
                                }} sx={{'&:hover': {cursor: 'pointer', bgcolor: theme.palette.navigation.selected}}} item>
                                    {/* <img src={InfoSvg} style={{width: '1.2em'}}/> */}
                                    <Typography fontSize="0.9em" style={{textDecoration: 'underline', textUnderlineOffset: '2px'}}
                                                color={theme.palette.text.primary}>FAQ</Typography>
                                </Grid>
                                <Grid display="flex" gap={2} onClick={() => logout()}
                                      sx={{'&:hover': {cursor: 'pointer', bgcolor: theme.palette.navigation.selected}}} item>
                                    {/* <img src={LogoutSvg} style={{width: '1.2em'}}/> */}
                                    <Typography fontSize="0.9em" 
                                                color={theme.palette.text.primary}>{t('dashboard.navigation.logOut')}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}